import { Ajax, CrudApiBase } from "../infra";
import { DatelyObject, Dates, OData, ODataQueryOptions, ODataResult } from "../../util";
import { Employment, EmployingRelation, EmploymentListItem } from "../../model";
import { EmploymentLogic } from "../../logic";

/**
 * Provides CRUD access to employment relations.
 */
export class Employments extends CrudApiBase<Employment, EmploymentListItem> {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL for details etc. */
  protected baseUrl = "/v03/api/employments";

  constructor(ajax: Ajax) {
    super(ajax);
  }

  /** Client-side (synchronous) method for getting a new blank item as bases for UI binding. */
  public getBlank(): Employment {
    return EmploymentLogic.getBlank();
  }

  /**
   * Makes an OData query to the employment relation list with helper to filter active relations.
   * @param activeDate The date for which the employment relations are fetched.
   * Use "all" for all relations (active or passive), "today" for relations that are active today or a date string for specific date.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public getODataActive(activeDate: "all" | "today" | DatelyObject, query: ODataQueryOptions | string): Promise<ODataResult<EmploymentListItem>> {
    if (activeDate != "today" && activeDate != "all") {
      activeDate = Dates.asDate(activeDate) ?? "all";
    }
    const baseUrl = this.getODataUrl() + "?activeDate=" + activeDate;
    return OData.getOData<EmploymentListItem>(baseUrl, query, this.ajax);
  }

  /**
   * Gets the active employment relations.
   * @param date The date for which the employment relations are fetched.
   * @returns A collection of full employment relation objects.
   */
  public getActiveFullObject(date: string): Promise<Employment[]> {
    let url = `${this.baseUrl}/active`;
    if (date) {
      url += `?date=${Dates.asDate(date)}`;
    }
    return this.ajax.getJSON(url);
  }

  /**
   * Returns a list of all employing relations of existing employers for the current account.
   */
  public getODataForEmployingRelations(options: ODataQueryOptions): Promise<ODataResult<any>> {
    const fullPath = OData.getUrl(`${this.baseUrl}/employing`, options);
    return this.ajax.getJSON(fullPath).then((data) => {
      return OData.getODataResult<any>(data);
    });
  }

  /**
   * Returns the public employment profile for the worker.
   * The profile contains for example project structure for the worker.
   * @param owner Employment owner.
   * @param id Employment id.
   */
  public getEmployingRelation(owner: string, id: string): Promise<EmployingRelation> {
    return this.ajax.getJSON(`${this.baseUrl}/employing/${id}@${owner}`);
  }
}
